// v3.15-988 — Internal Funding Closure Email + Manual Send Control.
// v3.15-981/982/988 confirmation gate, internal-only recipients (director /
// manager / sales rep), full wire/payment summary in preview, optional
// "Save Without Sending" path for historical reconciliations.
// v3.15-1063 — editable Subject input + override_subject on POST.
// v3.15-1064 — Subject Template dropdown (insert-only convenience; never
// locks the field, never auto-overwrites operator edits).
// Preserves: confirm:true protection, preview fetch, audit logging,
// APP_ENV [TEST EMAIL] prefix, large-recipient warning, internal-only
// delivery restriction.
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, Send, AlertTriangle, CheckCircle2, Pencil, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const splitEmails = (s) => (s || '')
  .split(/[\s,;]+/)
  .map((x) => x.trim())
  .filter((x) => x && /@/.test(x));

// v3.15-1064: Standardised Funding Closure subject lines. Operator picks one
// from the dropdown → the interpolated string is INSERTED into the editable
// Subject field. The field stays writable; any further edits the operator
// makes are authoritative and are what actually gets sent.
//
// Placeholders:
//   {Client Name}  → deal_name from /paid-in-full/preview
//   {Reference}    → wire_block.wire_reference (preview); if blank the
//                    "— Ref #{Reference}" suffix is dropped entirely so we
//                    never send a dangling "Ref #" with nothing after it.
const SUBJECT_TEMPLATES = [
  'Funding Complete — {Client Name}',
  'Deal Successfully Closed — {Client Name}',
  'Funding Confirmation — {Client Name}',
  'Wire Confirmation — {Client Name}',
  'Funding Complete — {Client Name} — Ref #{Reference}',
  'Paid In Full — {Client Name}',
];

const renderTemplate = (tpl, { clientName, reference }) => {
  const ref = (reference || '').trim();
  if (!ref) {
    // Drop trailing " — Ref #{Reference}" if reference is unavailable so we
    // never produce "... — Ref #" with an empty number after it.
    tpl = tpl.replace(/\s*[-—]\s*Ref\s+#\{Reference\}\s*$/i, '');
  }
  return tpl
    .replaceAll('{Client Name}', (clientName || '').trim() || 'Client')
    .replaceAll('{Reference}', ref);
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(v);
};

export const PaidInFullConfirmModal = ({ open, onOpenChange, clientId, onConfirmed, isHistorical = false }) => {
  const [loading, setLoading]   = useState(false);
  const [sending, setSending]   = useState(false);
  const [preview, setPreview]   = useState(null);
  const [editing, setEditing]   = useState(false);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  // v3.15-1063: editable subject (default = backend-generated; operator may
  // freely edit to add wire reference / lender name / confirmation number).
  const [subject, setSubject] = useState('');
  // v3.15-988: send toggle. OFF for historical/backfilled deals, ON for live.
  const [sendEmail, setSendEmail] = useState(!isHistorical);

  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    setLoading(true);
    setEditing(false);
    setPreview(null);
    setSubject('');
    setSendEmail(!isHistorical);
    api.get(`/clients/${clientId}/paid-in-full/preview`)
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.already) {
          toast.info('Deal was already marked paid in full');
          onOpenChange?.(false);
          return;
        }
        setPreview(data);
        setTo(data?.to || '');
        setCc((data?.cc || []).join(', '));
        // v3.15-1063: seed editable subject from backend default. From this
        // point on the operator's input is authoritative — we do NOT
        // re-seed on subsequent re-renders (only on a fresh modal open).
        setSubject(data?.subject || '');
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || 'Failed to load email preview');
        onOpenChange?.(false);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, clientId, onOpenChange, isHistorical]);

  const finalCcList = useMemo(() => splitEmails(cc), [cc]);
  const recipientCount = (to.trim() ? 1 : 0) + finalCcList.length;
  const showLargeWarning = recipientCount > 3;
  // v3.18-1305 — non-prod banner removed; app_env no longer inspected.
  const noInternalRecipients = !!preview?.no_internal_recipients;

  const submit = async ({ withEmail }) => {
    if (!clientId) return;
    if (withEmail) {
      if (!to.trim() || !/@/.test(to)) {
        toast.error('Valid "To" email is required');
        return;
      }
    }
    setSending(true);
    try {
      const res = await api.post(`/clients/${clientId}/paid-in-full`, {
        confirm: true,
        send_email: withEmail,
        override_to: withEmail ? to.trim() : '',
        override_cc: withEmail ? finalCcList : [],
        // v3.15-1063: send the exact operator-edited subject. Empty string
        // → backend falls back to its default. Only sent when email is on.
        override_subject: withEmail ? (subject || '').trim() : undefined,
      });
      if (res.data?.already) {
        toast.info('Deal was already marked paid in full');
      } else if (withEmail) {
        toast.success(`Deal closed — internal email sent (${finalCcList.length} CC)`);
      } else {
        toast.success('Deal closed — saved without sending email');
      }
      onConfirmed?.(res.data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to close deal');
    } finally {
      setSending(false);
    }
  };

  const wb = preview?.wire_block || {};
  const wireRows = [
    ['Wire Reference #',   wb.wire_reference],
    ['Date Sent',          wb.date_sent],
    ['Amount',             fmtMoney(wb.amount)],
    ['Deposit Date',       wb.deposit_date],
    ['Wire Transfer Fee',  fmtMoney(wb.wire_transfer_fee)],
    ['Paid to PTC',        fmtMoney(wb.paid_to_ptc)],
    ['Paid to Partner',    fmtMoney(wb.paid_to_partner)],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[92vh] overflow-y-auto" data-testid="paid-in-full-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Confirm Funding Closure
            <span className="ml-2 text-[11px] font-normal bg-slate-100 text-slate-600 px-2 py-0.5 rounded">INTERNAL ONLY</span>
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-12 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading preview…
          </div>
        )}

        {!loading && preview && (
          <div className="space-y-3 mt-2" data-testid="paid-in-full-preview-body">
            {/* v3.18-1305 — Non-production "[TEST EMAIL]" banner removed.
                Every outbound email now flows through a mandatory manual
                Confirm & Send review, so the auto-prefix warning was
                noise.  Prefixing has also been removed at the backend
                pipeline (services/email_signature layer + server
                _apply_test_email_prefix() no-op). */}
            {sendEmail && noInternalRecipients && (
              <div
                className="rounded-md border border-rose-300 bg-rose-50 text-rose-900 px-3 py-2 text-xs flex items-center gap-2"
                data-testid="paid-in-full-no-internal-warning"
              >
                <AlertTriangle className="h-4 w-4" />
                No assigned internal recipients found. Cannot send — assign a director/manager/rep
                first, or save without sending.
              </div>
            )}
            {sendEmail && showLargeWarning && (
              <div
                className="rounded-md border border-rose-300 bg-rose-50 text-rose-900 px-3 py-2 text-xs flex items-center gap-2"
                data-testid="paid-in-full-large-recipient-warning"
              >
                <AlertTriangle className="h-4 w-4" />
                Large recipient list detected ({recipientCount}). Please verify recipients before sending.
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <Label className="text-slate-500">Deal / Client</Label>
                <div className="mt-1 font-medium text-slate-800" data-testid="paid-in-full-deal-name">
                  {preview.deal_name}
                </div>
              </div>
              <div>
                <Label className="text-slate-500">Recipients</Label>
                <div className="mt-1 font-medium text-slate-800" data-testid="paid-in-full-recipient-count">
                  {recipientCount}
                </div>
              </div>
            </div>

            {/* v3.15-988: send-email toggle */}
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 flex items-center gap-2">
              <input
                id="send-funding-email-toggle"
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="h-4 w-4"
                data-testid="paid-in-full-send-email-toggle"
              />
              <label htmlFor="send-funding-email-toggle" className="text-xs text-slate-700 cursor-pointer">
                Send funding closure email <span className="text-slate-400">
                  (default {isHistorical ? 'OFF for historical/backfilled deals' : 'ON for live operational deals'})
                </span>
              </label>
            </div>

            <div>
              <Label className="text-xs text-slate-500">
                To <span className="text-slate-400">— assigned director / manager / sales rep only</span>
              </Label>
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={!editing || !sendEmail}
                className="h-9 text-sm"
                data-testid="paid-in-full-to"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">CC</Label>
              <Input
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                disabled={!editing || !sendEmail}
                placeholder="comma-separated emails (internal only)"
                className="h-9 text-sm font-mono"
                data-testid="paid-in-full-cc"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Subject</Label>
              {/* v3.15-1064: Template dropdown. Insert-only — populates the
                  Subject field, never locks it. Subsequent operator edits
                  win and are what gets sent. */}
              <select
                value=""
                onChange={(e) => {
                  const tpl = e.target.value;
                  if (!tpl) return;
                  const populated = renderTemplate(tpl, {
                    clientName: preview?.deal_name || '',
                    reference: preview?.wire_block?.wire_reference || '',
                  });
                  setSubject(populated);
                  // Reset the <select> back to placeholder so the next
                  // selection always fires a change even if it's the same
                  // template chosen again.
                  e.target.value = '';
                }}
                disabled={!editing || !sendEmail}
                className="mb-1.5 h-9 w-full rounded-md border border-slate-300 bg-white text-xs text-slate-700 px-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="paid-in-full-subject-template-select"
              >
                <option value="">— Insert a subject template —</option>
                {SUBJECT_TEMPLATES.map((tpl) => (
                  <option key={tpl} value={tpl} data-testid={`paid-in-full-subject-template-option-${tpl.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                    {tpl}
                  </option>
                ))}
              </select>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={!editing || !sendEmail}
                placeholder="Email subject — add wire ref / lender / confirmation # if needed"
                className="h-9 text-sm"
                data-testid="paid-in-full-subject"
              />
              {/* v3.15-1063: operator-edited subject becomes authoritative on send */}
              <p className="text-[10px] text-slate-400 mt-0.5">
                Default auto-populated. Edit freely or insert a template — your text will be sent verbatim.
              </p>
            </div>

            {/* v3.15-988: Wire / Payment summary block */}
            <div className="rounded-md border border-slate-200 bg-white px-3 py-3" data-testid="paid-in-full-wire-block">
              <div className="text-xs font-semibold text-slate-700 mb-2">Wire Transfer Info</div>
              <table className="text-xs w-full">
                <tbody>
                  {wireRows.map(([k, v]) => (
                    <tr key={k}>
                      <td className="py-1 pr-4 text-slate-500 align-top w-44">{k}</td>
                      <td className="py-1 text-slate-800" data-testid={`paid-in-full-wire-${k.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/,'')}`}>
                        {v && String(v).trim() ? v : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <Label className="text-xs text-slate-500">Body Preview</Label>
              <Textarea
                value={preview.body || ''}
                disabled
                className="text-sm min-h-[180px] font-mono"
                data-testid="paid-in-full-body"
              />
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-4 gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setEditing((v) => !v)}
            disabled={loading || sending || !sendEmail}
            data-testid="paid-in-full-edit-recipients-btn"
          >
            <Pencil className="h-4 w-4 mr-2" />
            {editing ? 'Done Editing' : 'Edit Recipients'}
          </Button>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={sending}
              data-testid="paid-in-full-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => submit({ withEmail: false })}
              disabled={loading || sending}
              className="border-slate-400"
              data-testid="paid-in-full-save-without-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Without Sending
            </Button>
            <Button
              onClick={() => submit({ withEmail: true })}
              disabled={loading || sending || !sendEmail || !to.trim() || noInternalRecipients}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="paid-in-full-confirm-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Confirm &amp; Send
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaidInFullConfirmModal;
